import type { AriaAttributes } from 'react';
import * as React from 'react';
import { forwardRef, useCallback } from 'react';
import type { ValidIconName } from './Icon';
import type { DefaultTooltipOptions } from '../../lib';
import { useSingleAndDoubleClick, useTooltipDefault } from '../../lib';
import type { ClassNameProp } from '..';
import { ButtonIcon } from './internal/ButtonIcon';

/**
 * Properties used for a {@link Tool}.
 */
export interface ToolProps
  extends ClassNameProp,
    DefaultTooltipOptions,
    Pick<AriaAttributes, 'aria-label' | 'aria-expanded'> {
  /**
   * Defines the look of the tool.
   */
  variant?: 'transparent' | 'gray' | 'white';
  /**
   * The tool icon.
   */
  icon: ValidIconName;
  /**
   * Tool click handler.
   */
  onClick: React.MouseEventHandler;
  /**
   * Optional tool text.
   */
  text?: string;
  /**
   * If the tool is disabled.
   */
  disabled?: boolean;
  /**
   * Tool tooltip.
   */
  tooltip?: string;
}

/**
 * A simple tool.
 *
 * @param properties properties for the tool. At least one of {text, tooltip} attributes is required
 * @constructor
 * @return the created {@link JSX.Element} for the tool
 */
export const Tool = forwardRef(function Tool(
  {
    variant,
    onClick,
    tooltip,
    tooltipOptions,
    className,
    disabled,
    text,
    icon,
    'aria-expanded': ariaExpanded,
    'aria-label': ariaLabel,
    ...props
  }: Readonly<
    ToolProps &
      (Required<Pick<ToolProps, 'tooltip'>> | Required<Pick<ToolProps, 'text'>>)
  >,
  ref
): JSX.Element {
  // create tooltip
  const [tooltipElement, triggerProps] = useTooltipDefault(
    tooltip,
    tooltipOptions
  );

  // create click handlers
  // click handler which automatically hides the tooltip
  const onClickHidingTooltip = useCallback(
    e => {
      triggerProps.onMouseLeave();
      onClick(e);
    },
    [onClick, triggerProps]
  );
  // passing no dblClickHandler will return a handler that simply stops propagation
  const [clickHandler, dblClickHandler] = useSingleAndDoubleClick(
    onClickHidingTooltip
  );

  const classes = ['ehs-tool'];
  if (!variant) {
    variant = 'transparent';
  }
  classes.push(variant);
  if (disabled) classes.push('disabled');
  if (className) classes.push(className);
  return (
    <>
      {tooltip ? tooltipElement : <></>}
      <button
        {...props}
        {...(tooltip && triggerProps)}
        className={classes.join(' ')}
        onClick={disabled ? undefined : clickHandler}
        onDoubleClick={dblClickHandler}
        aria-label={ariaLabel ?? (text || tooltip || undefined)}
        type="button"
        aria-expanded={ariaExpanded}
        disabled={disabled}
        aria-disabled={disabled ? true : undefined}
      >
        <ButtonIcon iconName={icon} />
        {text ? <span className={'tool-text'}>{text}</span> : <></>}
      </button>
    </>
  );
});
